import { useRef, useEffect, useImperativeHandle, forwardRef, useState } from "react";
import { Eraser, PenLine } from "lucide-react";

export const SignaturePad = forwardRef(
  ({ onChange, initialDataURL, label = "Firma", testid = "signature" }, ref) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPoint = useRef(null);
  const drewSinceClear = useRef(false);
  const [hasInk, setHasInk] = useState(!!initialDataURL);

  const drawInitial = () => {
    if (!initialDataURL) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, rect.width, rect.height);
    };
    img.src = initialDataURL;
  };

  const setupCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
    drawInitial();
  };

  useEffect(() => {
    setupCanvas();
    const handleResize = () => {
      // Preserve nothing on resize; just reset surface
      setupCanvas();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    lastPoint.current = getPos(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPoint.current = p;
    drewSinceClear.current = true;
    if (!hasInk) setHasInk(true);
  };

  const end = (e) => {
    if (!drawing.current) return;
    e && e.preventDefault();
    drawing.current = false;
    // Only emit when the user actually drew new strokes in this session.
    // This prevents overwriting a pre-loaded signature (edit mode) with a
    // blank/partial canvas captured before the initial image finished loading.
    if (drewSinceClear.current && onChange) {
      onChange(canvasRef.current.toDataURL("image/png"));
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    drewSinceClear.current = false;
    setHasInk(false);
    if (onChange) onChange("");
  };

  useImperativeHandle(ref, () => ({
    clear,
    isEmpty: () => !hasInk,
    getDataURL: () => (hasInk ? canvasRef.current.toDataURL("image/png") : ""),
  }));

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold tracking-wide uppercase text-slate-500 flex items-center gap-1.5">
          <PenLine className="h-4 w-4" /> {label}
        </span>
        <button
          type="button"
          onClick={clear}
          data-testid={`clear-${testid}`}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-red-600 transition-colors"
        >
          <Eraser className="h-4 w-4" /> Borrar
        </button>
      </div>
      <canvas
        ref={canvasRef}
        data-testid={`${testid}-canvas`}
        className="w-full h-[200px] rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 touch-none cursor-crosshair"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      {!hasInk && (
        <p className="mt-1.5 text-xs text-slate-400">Firme con el dedo (móvil) o el ratón (ordenador).</p>
      )}
    </div>
  );
});

SignaturePad.displayName = "SignaturePad";
